import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Shield, CheckCircle, Clock, Landmark, Truck, Package, Lock, AlertCircle, CreditCard, MapPin, Globe, FileText, ArrowRight } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;
const SL = { pending_payment: "Pending Execution", payment_received: "Settlement Secured", processing: "Execution in Progress", shipped: "Shipment Dispatched", in_transit: "In Transit", delivered: "Delivered — Settlement Triggered", completed: "Settlement Released", disputed: "Settlement Held — Under Review" };
const statusSteps = ["pending_payment", "payment_received", "processing", "shipped", "in_transit", "delivered", "completed"];
function fmtAmt(a, c) { return new Intl.NumberFormat("en-US", { style: "currency", currency: c || "USD", minimumFractionDigits: 0 }).format(a); }

export default function BuyerPaymentPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [txn, setTxn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(false);

  const fetchTxn = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/t/${token}`); setTxn(data); }
    catch { setError("Transaction not found or link has expired."); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchTxn(); }, [fetchTxn]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId || !txn) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) { setPolling(false); return; }
      try {
        const { data } = await axios.get(`${API}/checkout-status/${sessionId}`);
        if (data.payment_status === "paid") { setPolling(false); fetchTxn(); return; }
        if (data.status === "expired") { setPolling(false); return; }
      } catch { /* retry */ }
      attempts++;
      setTimeout(poll, 2500);
    };
    poll();
  }, [searchParams, txn, fetchTxn]);

  const handlePay = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/t/${token}/checkout`, { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (err) { console.error(err); setPaying(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#050A07] flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#22C55E] border-t-transparent rounded-full animate-spin" /></div>;
  if (error) return (
    <div className="min-h-screen bg-[#050A07] flex items-center justify-center px-6">
      <div className="text-center max-w-md"><Lock className="w-12 h-12 text-[#A1B2A8]/30 mx-auto mb-4" /><h1 className="font-['Outfit'] text-xl font-bold text-white mb-2">Access Denied</h1><p className="text-[#A1B2A8] text-sm">{error}</p></div>
    </div>
  );

  const currentIdx = statusSteps.indexOf(txn.status);
  const isPending = txn.status === "pending_payment";
  const isDisputed = txn.status === "disputed";
  const refId = `CF-${txn.id.slice(0, 8).toUpperCase()}`;

  return (
    <div className="min-h-screen bg-[#050A07]" data-testid="buyer-payment-page">
      {/* Header */}
      <header className="border-b border-[#1A2E22] bg-[#050A07]/90 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandLogo as="div" size="md" />
          <div className="flex items-center gap-3">
            <span className="text-[#A1B2A8]/40 text-xs font-mono">{refId}</span>
            <span className="text-[#A1B2A8] text-xs flex items-center gap-1"><Lock className="w-3 h-3" /> Trade Execution Infrastructure</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {polling && (
          <div className="bg-blue-400/10 border border-blue-400/30 p-4 mb-6 flex items-center gap-3" data-testid="polling-status">
            <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            <p className="text-blue-400 text-sm">Confirming capital transfer... This may take a moment.</p>
          </div>
        )}

        {/* Amount Hero Card */}
        <div className="bg-[#09120D] border border-[#1A2E22] p-8 mb-6 relative overflow-hidden" data-testid="txn-header">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#22C55E]/[0.02] rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative z-10 text-center">
            <p className="text-[#A1B2A8] text-sm mb-1">Trade Flow Amount</p>
            <p className="font-['Outfit'] text-5xl sm:text-6xl font-bold text-white tracking-tight">{fmtAmt(txn.amount, txn.currency)}</p>
            <div className="mt-4 inline-flex items-center gap-2 bg-[#050A07] border border-[#1A2E22] px-4 py-2">
              <Landmark className={`w-4 h-4 ${isDisputed ? "text-red-400" : "text-[#22C55E]"}`} />
              <span className={`text-sm font-medium ${isDisputed ? "text-red-400" : "text-[#22C55E]"}`}>{SL[txn.status] || txn.status}</span>
            </div>
            {txn.capital_structure && <p className="text-[#A1B2A8]/40 text-xs mt-3">Structure: {txn.capital_structure} &middot; {txn.currency}</p>}
          </div>
        </div>

        {/* Pay Button */}
        {isPending && (
          <button onClick={handlePay} disabled={paying} className="w-full bg-[#16A34A] text-white hover:bg-[#15803D] transition-all py-4 font-medium font-['Outfit'] text-lg mb-6 disabled:opacity-50 flex items-center justify-center gap-3 group" data-testid="pay-now-btn">
            <CreditCard className="w-5 h-5" />
            {paying ? "Redirecting to secure settlement..." : "Execute Trade Settlement"}
            {!paying && <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />}
          </button>
        )}

        {/* Trust & Security Badges */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
          {[
            { icon: Shield, label: "Verified Exporter", sub: txn.supplier_name?.split(" ")[0] },
            { icon: Lock, label: "Settlement Protected", sub: "End-to-end" },
            { icon: CheckCircle, label: "Trust Verified", sub: "Milestone-linked" },
            { icon: Landmark, label: txn.capital_structure || "Structured", sub: txn.currency },
          ].map((b, i) => (
            <div key={i} className="bg-[#09120D] border border-[#1A2E22] px-4 py-3 text-center">
              <b.icon className="w-4 h-4 text-[#22C55E] mx-auto mb-1.5" />
              <p className="text-[#A1B2A8] text-xs">{b.label}</p>
              <p className="text-[#A1B2A8]/30 text-[10px]">{b.sub}</p>
            </div>
          ))}
        </div>

        {/* Buyer Trust Explanation */}
        {isPending && (
          <div className="bg-[#09120D] border border-[#22C55E]/20 p-6 mb-6" data-testid="trust-explanation">
            <h2 className="font-['Outfit'] text-sm font-semibold text-[#22C55E] mb-4 uppercase tracking-wider">How Your Trade Is Secured</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#22C55E]/10 flex items-center justify-center flex-shrink-0 mt-0.5"><Lock className="w-3 h-3 text-[#22C55E]" /></div>
                <div><p className="text-white text-sm font-medium">Controlled Trade Flow</p><p className="text-[#A1B2A8]/60 text-xs mt-0.5">Your settlement is held in a structured flow. Funds are only released to the exporter after delivery is confirmed and verified.</p></div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#22C55E]/10 flex items-center justify-center flex-shrink-0 mt-0.5"><Shield className="w-3 h-3 text-[#22C55E]" /></div>
                <div><p className="text-white text-sm font-medium">Verified Exporter</p><p className="text-[#A1B2A8]/60 text-xs mt-0.5">{txn.supplier_name} has been verified through Delta GreenX's trust and compliance process{txn.risk_score ? ` with an AI confidence score of ${txn.risk_score}/100` : ""}.</p></div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#22C55E]/10 flex items-center justify-center flex-shrink-0 mt-0.5"><FileText className="w-3 h-3 text-[#22C55E]" /></div>
                <div><p className="text-white text-sm font-medium">Milestone-Based Execution</p><p className="text-[#A1B2A8]/60 text-xs mt-0.5">Settlement release is tied to shipment milestones — from execution commencement through dispatch, transit, and delivery confirmation.</p></div>
              </div>
            </div>
          </div>
        )}

        {/* Parties & Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <div className="bg-[#09120D] border border-[#1A2E22] p-5">
            <div className="flex items-center gap-2 mb-2"><Globe className="w-3.5 h-3.5 text-[#22C55E]" /><p className="text-[#A1B2A8] text-xs uppercase tracking-wider">Supplier</p></div>
            <p className="text-white text-sm font-semibold">{txn.supplier_name}</p>
            <p className="text-[#A1B2A8]/50 text-xs mt-0.5">{txn.supplier_company}</p>
            {txn.risk_score && <div className="mt-2 flex items-center gap-1"><Shield className={`w-3 h-3 ${txn.risk_score >= 80 ? "text-[#22C55E]" : "text-yellow-400"}`} /><span className="text-[#A1B2A8]/60 text-xs">Risk Score: {txn.risk_score}</span></div>}
          </div>
          <div className="bg-[#09120D] border border-[#1A2E22] p-5">
            <div className="flex items-center gap-2 mb-2"><MapPin className="w-3.5 h-3.5 text-[#22C55E]" /><p className="text-[#A1B2A8] text-xs uppercase tracking-wider">Buyer</p></div>
            <p className="text-white text-sm font-semibold">{txn.buyer_name}</p>
            <p className="text-[#A1B2A8]/50 text-xs mt-0.5">{txn.buyer_company}</p>
          </div>
        </div>

        {/* Transaction Details */}
        <div className="bg-[#09120D] border border-[#1A2E22] p-6 mb-6" data-testid="txn-details">
          <h2 className="font-['Outfit'] text-sm font-semibold text-white mb-4 uppercase tracking-wider">Trade Flow Details</h2>
          <div className="space-y-3">
            {[
              ["Reference", refId],
              ["Description", txn.description],
              ["Capital Structure", txn.capital_structure || txn.payment_method],
              ["Amount", fmtAmt(txn.amount, txn.currency)],
              ["Currency", txn.currency],
              ["Initiated", new Date(txn.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })],
              ["Last Updated", new Date(txn.updated_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-[#1A2E22] pb-3 last:border-0 last:pb-0">
                <span className="text-[#A1B2A8] text-sm">{k}</span>
                <span className="text-white text-sm text-right max-w-[60%] font-mono text-xs">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Capital Release Triggers */}
        {txn.trigger_conditions && txn.trigger_conditions.length > 0 && (
          <div className="bg-[#09120D] border border-[#1A2E22] p-6 mb-6" data-testid="trigger-conditions">
            <h2 className="font-['Outfit'] text-sm font-semibold text-white mb-4 uppercase tracking-wider">Settlement Release Triggers</h2>
            <div className="space-y-4">
              {txn.trigger_conditions.map((tc, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 border ${tc.status === "completed" ? "bg-[#22C55E] border-[#22C55E]" : "bg-transparent border-[#1A2E22]"}`}>
                      {tc.status === "completed" ? <CheckCircle className="w-4 h-4 text-white" /> : <span className="text-[#A1B2A8]/40 text-xs font-mono">{i + 1}</span>}
                    </div>
                    {i < txn.trigger_conditions.length - 1 && <div className={`w-px h-6 ${tc.status === "completed" ? "bg-[#22C55E]/30" : "bg-[#1A2E22]"}`} />}
                  </div>
                  <div className="pt-1">
                    <p className={`text-sm font-medium ${tc.status === "completed" ? "text-white" : "text-[#A1B2A8]/40"}`}>{tc.milestone}</p>
                    <p className={`text-xs mt-0.5 ${tc.status === "completed" ? "text-[#A1B2A8]/60" : "text-[#A1B2A8]/20"}`}>{tc.action}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status Progress Bar */}
        {!isDisputed && (
          <div className="bg-[#09120D] border border-[#1A2E22] p-6 mb-6" data-testid="status-progress">
            <h2 className="font-['Outfit'] text-sm font-semibold text-white mb-5 uppercase tracking-wider">Trade Flow Progress</h2>
            <div className="flex items-center justify-between relative">
              <div className="absolute top-3 left-0 right-0 h-0.5 bg-[#1A2E22]" />
              <div className="absolute top-3 left-0 h-0.5 bg-[#22C55E] transition-all duration-500" style={{ width: `${Math.max(0, (currentIdx / (statusSteps.length - 1)) * 100)}%` }} />
              {statusSteps.map((step, i) => (
                <div key={step} className="relative z-10 flex flex-col items-center">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs transition-all ${i <= currentIdx ? "bg-[#22C55E] text-white" : "bg-[#1A2E22] text-[#A1B2A8]"}`}>
                    {i <= currentIdx ? <CheckCircle className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <span className={`text-[10px] mt-2 text-center max-w-[60px] leading-tight ${i <= currentIdx ? "text-[#22C55E]" : "text-[#A1B2A8]/30"}`}>
                    {(SL[step] || step).split(" ").slice(-1)[0]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timeline */}
        {txn.timeline && txn.timeline.length > 0 && (
          <div className="bg-[#09120D] border border-[#1A2E22] p-6" data-testid="shipment-timeline">
            <h2 className="font-['Outfit'] text-sm font-semibold text-white mb-5 uppercase tracking-wider">Execution Timeline</h2>
            <div className="space-y-0">
              {[...txn.timeline].reverse().map((e, i) => (
                <div key={i} className="flex gap-4 relative">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${i === 0 ? "bg-[#22C55E] ring-4 ring-[#22C55E]/10" : "bg-[#1A2E22]"}`} />
                    {i < txn.timeline.length - 1 && <div className="w-px h-full bg-[#1A2E22] min-h-[48px]" />}
                  </div>
                  <div className="pb-6">
                    <p className={`text-sm font-medium ${i === 0 ? "text-white" : "text-[#A1B2A8]"}`}>{e.event}</p>
                    <p className="text-[#A1B2A8]/60 text-xs mt-1">{e.description}</p>
                    <p className="text-[#A1B2A8]/25 text-xs mt-1.5 font-mono">{new Date(e.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Security Footer */}
      <footer className="border-t border-[#1A2E22] py-8 mt-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Lock className="w-4 h-4 text-[#22C55E]/40" />
              <p className="text-[#A1B2A8]/40 text-xs">256-bit encrypted &middot; SOC 2 compliant &middot; PCI DSS Level 1</p>
            </div>
            <p className="text-[#A1B2A8]/25 text-xs">Delta GreenX — Operating Infrastructure for Global Food Trade &middot; {refId}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
